import type { BriefAccessRoleType } from 'bc-model';
import type { ReactElement } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import {
  roleAccessLabelMessages,
  ROLE_TO_ACCESS_LABEL_MESSAGE,
  userPermissionOptions,
} from './utils';
import { ODSSelect } from 'bc-ods';
import { useMemo } from 'react';
import type { RoleOption } from './types';

const messages = defineMessages({
  roleAccess: {
    id: 'PlanningTeam.roleAccess',
    defaultMessage:
      '{role, select, owner {Owner} admin {Administrator} editor {Editor} commenter {Commenter} reader {Reader} other {Unknown}}',
  },
});

type RoleDropdownProps = {
  role: BriefAccessRoleType;
  restrictToOnlyReader?: boolean;
  setRole: (role: BriefAccessRoleType) => void;
  size?: 'sm' | 'md' | 'lg';
  label?: string;
  useAccessLabels?: boolean;
};

const RoleDropdown = ({
  role,
  restrictToOnlyReader,
  setRole,
  label,
  size = 'lg',
  useAccessLabels = false,
}: RoleDropdownProps): ReactElement => {
  const intl = useIntl();

  const roleOptions = useMemo<RoleOption[]>(() => {
    return userPermissionOptions.map((roleOption) => ({
      value: roleOption,
      label: useAccessLabels
        ? intl.formatMessage(roleAccessLabelMessages[ROLE_TO_ACCESS_LABEL_MESSAGE[roleOption]])
        : intl.formatMessage(messages.roleAccess, { role: roleOption }),
    }));
  }, [intl, useAccessLabels]);

  const onlyReaderOption = useMemo<RoleOption[]>(() => {
    return [
      {
        value: 'reader',
        label: useAccessLabels
          ? intl.formatMessage(roleAccessLabelMessages[ROLE_TO_ACCESS_LABEL_MESSAGE.reader])
          : intl.formatMessage(messages.roleAccess, { role: 'reader' }),
      },
    ];
  }, [intl, useAccessLabels]);

  const selectedRole = useMemo<RoleOption>(() => {
    return {
      value: role,
      label: useAccessLabels
        ? intl.formatMessage(roleAccessLabelMessages[ROLE_TO_ACCESS_LABEL_MESSAGE[role]])
        : intl.formatMessage(messages.roleAccess, { role }),
    };
  }, [intl, role, useAccessLabels]);

  const availableOptions = restrictToOnlyReader ? onlyReaderOption : roleOptions;

  return (
    <ODSSelect
      label={label}
      size={size}
      isClearable={false}
      onChange={(option) => {
        if (option) setRole(option.value);
      }}
      value={selectedRole}
      options={availableOptions}
    />
  );
};

export default RoleDropdown;
